/**
 * Star Ratings Dashboard - Enterprise Grade
 * Real-time tracking of Aged Care Star Ratings components
 */

import React, { useState, useEffect } from 'react';
import { 
  Star, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Download, RefreshCw, BarChart3, Target, Award, TrendingUp as TrendingUpIcon,
  FileText, Settings, Eye, ArrowRight, ArrowLeft
} from 'lucide-react';
import StarRatingsService from '../../services/quality/StarRatingsService';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';

const StarRatingsDashboard = ({ facilityId }) => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = facilityId || state.selectedFacility;
  
  const [rating, setRating] = useState(null);
  const [impactAnalysis, setImpactAnalysis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showComponentDetail, setShowComponentDetail] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  const loadData = async () => {
    setLoading(true);
    try {
      const currentResult = await StarRatingsService.getCurrentRating(selectedFacility);
      const impactResult = await StarRatingsService.getImpactAnalysis(selectedFacility);
      const historyResult = await StarRatingsService.getRatingHistory(selectedFacility);
      
      if (currentResult.success) {
        setRating(currentResult.data);
      } else {
        // Auto-calculate if no rating exists
        const calcResult = await StarRatingsService.autoCalculate(selectedFacility);
        if (calcResult.success) {
          setRating(calcResult.data);
        }
      }
      
      if (impactResult.success) {
        setImpactAnalysis(impactResult.data);
      }
      
      if (historyResult.success) {
        setHistory(historyResult.data.slice(0, 12)); // Last 12 ratings
      }
    } catch (error) {
      console.error('Error loading star ratings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setLoading(true);
    try {
      const result = await StarRatingsService.autoCalculate(selectedFacility);
      if (result.success) {
        await loadData();
        showToast('Star rating recalculated successfully', 'success');
      }
    } catch (error) {
      console.error('Error recalculating:', error);
      showToast('Error recalculating star rating', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const handleExportData = async (format) => {
    try {
      const data = {
        facilityId: selectedFacility,
        rating,
        history,
        impactAnalysis,
        exportedAt: new Date().toISOString()
      };

      if (format === 'csv') {
        const csv = generateCSV(data);
        downloadFile(csv, `star-ratings-${selectedFacility}.csv`, 'text/csv');
      } else if (format === 'pdf') {
        showToast('PDF export functionality coming soon', 'info');
      } else {
        const json = JSON.stringify(data, null, 2);
        downloadFile(json, `star-ratings-${selectedFacility}.json`, 'application/json');
      }
      
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting data:', error);
      showToast('Error exporting data', 'error');
    }
  };

  const generateCSV = (data) => {
    const headers = ['Date', 'Overall Rating', 'Experience', 'Compliance', 'Staffing', 'Quality'];
    const rows = data.history.map(h => [
      h.assessmentDate,
      h.overallRating,
      h.residentsExperience.score,
      h.compliance.score,
      h.staffing.score,
      h.qualityMeasures.score
    ].join(','));
    
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading && !rating) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!rating) {
    return (
      <div className="text-center p-8">
        <Star className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-lg text-gray-600 mb-4">No star rating data available</p>
        <button
          onClick={handleRecalculate}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
        >
          Calculate Star Rating
        </button>
      </div>
    );
  }

  const renderStars = (score) => {
    const stars = [];
    const fullStars = Math.floor(score);
    const hasHalfStar = score % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<Star key={i} className="w-6 h-6 text-yellow-400 fill-current opacity-50" />);
      } else {
        stars.push(<Star key={i} className="w-6 h-6 text-gray-300" />);
      }
    }
    
    return stars;
  };

  const getRatingColor = (score) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 4.0) return 'text-blue-600';
    if (score >= 3.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRatingBadge = (score) => {
    if (score >= 4.5) return { text: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (score >= 4.0) return { text: 'Good', color: 'bg-blue-100 text-blue-800' };
    if (score >= 3.5) return { text: 'Satisfactory', color: 'bg-yellow-100 text-yellow-800' };
    return { text: 'Needs Improvement', color: 'bg-red-100 text-red-800' };
  };

  const badge = getRatingBadge(rating.overallRating);

  return (
    <div className="space-y-6">
      {/* Header with Overall Rating */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
                <Award className="w-8 h-8" />
                Aged Care Star Rating
              </h1>
              <p className="text-purple-100 text-lg">Overall facility quality rating</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRecalculate}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all backdrop-blur-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Recalculate
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all backdrop-blur-sm"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              {renderStars(rating.overallRating)}
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">{rating.overallRating.toFixed(1)}</div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
                  {badge.text}
                </span>
                <span className="text-purple-100 text-sm">
                  Last updated: {new Date(rating.assessmentDate).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-sm text-purple-100 mb-1">vs National Average</div>
              <div className="text-2xl font-bold">
                {rating.overallRating > rating.benchmarks.national ? '+' : ''}
                {(rating.overallRating - rating.benchmarks.national).toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Component Breakdown - Enhanced */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Residents' Experience (33%) */}
        <div 
          className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-blue-300 transition-all cursor-pointer"
          onClick={() => setShowComponentDetail('experience')}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 text-lg">Residents' Experience</h3>
            <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">
              33%
            </div>
          </div>
          <div className="flex items-center gap-2 mb-3">
            {renderStars(rating.residentsExperience.score)}
          </div>
          <div className={`text-3xl font-bold mb-2 ${getRatingColor(rating.residentsExperience.score)}`}>
            {rating.residentsExperience.score.toFixed(1)}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Weighted Contribution</span>
              <span className="font-semibold text-gray-800">
                {(rating.residentsExperience.score * rating.residentsExperience.weight).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Consumer Survey</span>
              <span className="font-semibold">{rating.residentsExperience.sources.consumerSurvey.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Family Feedback</span>
              <span className="font-semibold">{rating.residentsExperience.sources.familyFeedback.toFixed(1)}</span>
            </div>
          </div>
          <button className="mt-4 w-full px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            View Details
          </button>
        </div>

        {/* Compliance (30%) */}
        <div 
          className={`bg-white rounded-xl p-6 shadow-lg border-2 ${
            rating.compliance.capsOverall ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-green-300'
          } transition-all cursor-pointer`}
          onClick={() => setShowComponentDetail('compliance')}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 text-lg">Compliance</h3>
            <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
              30%
            </div>
          </div>
          {rating.compliance.capsOverall && (
            <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded-lg">
              <div className="flex items-center gap-2 text-red-800 text-xs font-semibold">
                <AlertTriangle className="w-4 h-4" />
                Caps Overall Rating
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            {renderStars(rating.compliance.score)}
          </div>
          <div className={`text-3xl font-bold mb-2 ${getRatingColor(rating.compliance.score)}`}>
            {rating.compliance.score.toFixed(1)}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Weighted Contribution</span>
              <span className="font-semibold text-gray-800">
                {(rating.compliance.score * rating.compliance.weight).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Quality Standards</span>
              <span className="font-semibold">{rating.compliance.sources.qualityStandards.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">SIRS Compliance</span>
              <span className="font-semibold text-green-600">{rating.compliance.sources.sirsCompliance.toFixed(1)}</span>
            </div>
          </div>
          <button className="mt-4 w-full px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            View Details
          </button>
        </div>

        {/* Staffing (22%) */}
        <div 
          className={`bg-white rounded-xl p-6 shadow-lg border-2 ${
            !rating.staffing.meetsBothTargets ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 hover:border-purple-300'
          } transition-all cursor-pointer`}
          onClick={() => setShowComponentDetail('staffing')}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 text-lg">Staffing</h3>
            <div className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
              22%
            </div>
          </div>
          {!rating.staffing.meetsBothTargets && (
            <div className="mb-3 p-2 bg-yellow-100 border border-yellow-300 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800 text-xs font-semibold">
                <AlertTriangle className="w-4 h-4" />
                Targets Not Met
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            {renderStars(rating.staffing.score)}
          </div>
          <div className={`text-3xl font-bold mb-2 ${getRatingColor(rating.staffing.score)}`}>
            {rating.staffing.score.toFixed(1)}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Weighted Contribution</span>
              <span className="font-semibold text-gray-800">
                {(rating.staffing.score * rating.staffing.weight).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Care Minutes</span>
              <span className="font-semibold text-green-600">
                {rating.staffing.sources.careMinutesCompliance.toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">RN Minutes</span>
              <span className="font-semibold text-green-600">
                {rating.staffing.sources.rnMinutesCompliance.toFixed(1)}
              </span>
            </div>
          </div>
          <button className="mt-4 w-full px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            View Details
          </button>
        </div>

        {/* Quality Measures (15%) */}
        <div 
          className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-indigo-300 transition-all cursor-pointer"
          onClick={() => setShowComponentDetail('quality')}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-800 text-lg">Quality Measures</h3>
            <div className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-semibold">
              15%
            </div>
          </div>
          <div className="flex items-center gap-2 mb-3">
            {renderStars(rating.qualityMeasures.score)}
          </div>
          <div className={`text-3xl font-bold mb-2 ${getRatingColor(rating.qualityMeasures.score)}`}>
            {rating.qualityMeasures.score.toFixed(1)}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Weighted Contribution</span>
              <span className="font-semibold text-gray-800">
                {(rating.qualityMeasures.score * rating.qualityMeasures.weight).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">NQIP Indicators</span>
              <span className="font-semibold">{rating.qualityMeasures.sources.nqipIndicators.toFixed(1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Safety Indicators</span>
              <span className="font-semibold">{rating.qualityMeasures.sources.safetyIndicators.toFixed(1)}</span>
            </div>
          </div>
          <button className="mt-4 w-full px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            View Details
          </button>
        </div>
      </div>

      {/* Benchmark Comparison */}
      <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-blue-600" />
          Benchmark Comparison
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-sm text-gray-600 mb-2">National Average</div>
            <div className="text-3xl font-bold text-blue-600 mb-2">{rating.benchmarks.national.toFixed(1)}</div>
            <div className="flex items-center gap-2">
              {rating.overallRating > rating.benchmarks.national ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-semibold">
                    +{(rating.overallRating - rating.benchmarks.national).toFixed(1)} above
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-600 font-semibold">
                    {(rating.overallRating - rating.benchmarks.national).toFixed(1)} below
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="text-sm text-gray-600 mb-2">State Average</div>
            <div className="text-3xl font-bold text-green-600 mb-2">{rating.benchmarks.state.toFixed(1)}</div>
            <div className="flex items-center gap-2">
              {rating.overallRating > rating.benchmarks.state ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-semibold">
                    +{(rating.overallRating - rating.benchmarks.state).toFixed(1)} above
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-600 font-semibold">
                    {(rating.overallRating - rating.benchmarks.state).toFixed(1)} below
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-sm text-gray-600 mb-2">Peer Group</div>
            <div className="text-3xl font-bold text-purple-600 mb-2">{rating.benchmarks.peerGroup.toFixed(1)}</div>
            <div className="flex items-center gap-2">
              {rating.overallRating > rating.benchmarks.peerGroup ? (
                <>
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-semibold">
                    +{(rating.overallRating - rating.benchmarks.peerGroup).toFixed(1)} above
                  </span>
                </>
              ) : (
                <>
                  <TrendingDown className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-600 font-semibold">
                    {(rating.overallRating - rating.benchmarks.peerGroup).toFixed(1)} below
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Impact Analysis */}
      {impactAnalysis.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              Impact Analysis & Recommendations
            </h3>
            <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
              {impactAnalysis.length} Issues
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {impactAnalysis.map((impact, index) => (
              <div
                key={index}
                className={`p-5 rounded-lg border-2 ${
                  impact.priority === 'CRITICAL'
                    ? 'bg-red-50 border-red-300'
                    : impact.priority === 'HIGH'
                    ? 'bg-orange-50 border-orange-300'
                    : 'bg-yellow-50 border-yellow-300'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-gray-800 text-lg mb-1">{impact.component}</h4>
                    <p className="text-sm text-gray-700">{impact.issue}</p>
                  </div>
                  <span
                    className={`px-3 py-1 text-xs rounded-full font-bold ${
                      impact.priority === 'CRITICAL'
                        ? 'bg-red-200 text-red-900'
                        : impact.priority === 'HIGH'
                        ? 'bg-orange-200 text-orange-900'
                        : 'bg-yellow-200 text-yellow-900'
                    }`}
                  >
                    {impact.priority}
                  </span>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Recommended Actions:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {impact.recommendations.map((rec, i) => (
                      <li key={i}>{rec}</li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => {
                    showToast(`Action plan for ${impact.component} coming soon`, 'info');
                  }}
                  className="mt-4 w-full px-4 py-2 bg-white hover:bg-gray-50 rounded-lg text-sm font-semibold text-gray-700 border border-gray-300 transition-all"
                >
                  Create Action Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historical Trend Chart */}
      {history.length > 1 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <TrendingUpIcon className="w-6 h-6 text-blue-600" />
              Historical Trend (Last 12 Assessments)
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Navigate to detailed trend view
                  showToast('Detailed trend analysis coming soon', 'info');
                }}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold"
              >
                View Full History
              </button>
            </div>
          </div>
          
          {/* Trend Visualization */}
          <div className="mb-6">
            <div className="h-48 flex items-end justify-between gap-1">
              {history.slice().reverse().map((h, index) => {
                const height = (h.overallRating / 5) * 100;
                const isIncreasing = index > 0 && h.overallRating > history[history.length - index].overallRating;
                return (
                  <div key={h.id} className="flex-1 flex flex-col items-center group">
                    <div
                      className={`w-full rounded-t-lg transition-all cursor-pointer group-hover:opacity-80 ${
                        isIncreasing ? 'bg-gradient-to-t from-green-500 to-green-400' : 'bg-gradient-to-t from-blue-500 to-blue-400'
                      }`}
                      style={{ height: `${height}%` }}
                      title={`${new Date(h.assessmentDate).toLocaleDateString()}: ${h.overallRating.toFixed(1)} stars`}
                    ></div>
                    <div className="text-xs text-gray-600 mt-2 text-center">
                      {new Date(h.assessmentDate).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Trend Summary */}
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Current</div>
              <div className="text-2xl font-bold text-blue-600">{rating.overallRating.toFixed(1)}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">6 Months Ago</div>
              <div className="text-2xl font-bold text-gray-600">
                {history.length > 5 ? history[history.length - 6].overallRating.toFixed(1) : 'N/A'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-1">Change</div>
              <div className={`text-2xl font-bold ${
                history.length > 5 && rating.overallRating > history[history.length - 6].overallRating
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}>
                {history.length > 5 ? (
                  <>
                    {rating.overallRating > history[history.length - 6].overallRating ? '+' : ''}
                    {(rating.overallRating - history[history.length - 6].overallRating).toFixed(1)}
                  </>
                ) : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component Detail Modal */}
      {showComponentDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 capitalize">
                {showComponentDetail} Component Details
              </h2>
              <button
                onClick={() => setShowComponentDetail(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            {showComponentDetail === 'experience' && (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Source Scores</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Consumer Survey</span>
                      <span className="font-bold">{rating.residentsExperience.sources.consumerSurvey.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Family Feedback</span>
                      <span className="font-bold">{rating.residentsExperience.sources.familyFeedback.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Satisfaction Metrics</span>
                      <span className="font-bold">{rating.residentsExperience.sources.satisfactionMetrics.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
                  View Survey Responses
                </button>
              </div>
            )}

            {showComponentDetail === 'compliance' && (
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Source Scores</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Quality Standards</span>
                      <span className="font-bold">{rating.compliance.sources.qualityStandards.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Audit Results</span>
                      <span className="font-bold">{rating.compliance.sources.auditResults.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>SIRS Compliance</span>
                      <span className="font-bold text-green-600">{rating.compliance.sources.sirsCompliance.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                  View Compliance Reports
                </button>
              </div>
            )}

            {showComponentDetail === 'staffing' && (
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Source Scores</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Care Minutes Compliance</span>
                      <span className="font-bold text-green-600">{rating.staffing.sources.careMinutesCompliance.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>RN Minutes Compliance</span>
                      <span className="font-bold text-green-600">{rating.staffing.sources.rnMinutesCompliance.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Staffing Levels</span>
                      <span className="font-bold">{rating.staffing.sources.staffingLevels.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Turnover Rate</span>
                      <span className="font-bold">{rating.staffing.sources.turnoverRate.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-800">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-semibold">
                      {rating.staffing.meetsBothTargets ? 'Both care minutes targets met' : 'Care minutes targets not fully met'}
                    </span>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold">
                  View Staffing Dashboard
                </button>
              </div>
            )}

            {showComponentDetail === 'quality' && (
              <div className="space-y-4">
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Source Scores</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>NQIP Indicators</span>
                      <span className="font-bold">{rating.qualityMeasures.sources.nqipIndicators.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Clinical Metrics</span>
                      <span className="font-bold">{rating.qualityMeasures.sources.clinicalMetrics.toFixed(1)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Safety Indicators</span>
                      <span className="font-bold">{rating.qualityMeasures.sources.safetyIndicators.toFixed(1)}</span>
                    </div>
                  </div>
                </div>
                <button className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold">
                  View Quality Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-purple-600" />
                Export Star Ratings
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExportData('csv')}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => handleExportData('json')}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
              <button
                onClick={() => handleExportData('pdf')}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <FileText className="w-5 h-5" />
                Export as PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StarRatingsDashboard;
